/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import Lottie from 'lottie-react';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  selectCurrentTeam,
  selectIsActionsBlocked,
  selectIsAdmin,
  selectIsOwner,
  showNotifier
} from 'containers/app/appSlice';
import { useCommunicationsControl } from 'containers/communications';
import { checkFeatureStatus } from '../card-on-file/utils';
import SidePanel from 'components/side-panel/SidePanel';
import { SharePendingInvoiceMainSection } from './SharePendingInvoiceMainSection';
import { EditAmountMainSection } from './EditAmountMainSection';
import { useCreateCustomAmountTrackingMutation, useLazyDownloadInvoiceAsPDFQuery } from '../api';
import { useGetInvoiceDataById } from '../hooks/useGetInvoiceDataById';
import { ArrowRightIcon, CrossIconvector } from 'assets/icons';
import { DEFAULT_SIDE_PANEL_ROUTE_STATE, FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { CollectPaymentRouteState } from 'routes/types';
import ManualKeyInModal from '../manual-key-in/ManualKeyInModal';
import { NotifierTypes } from 'constants/notifierConstants';
import { useGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { formatAmount, formatDecimal, getAmountWithCurrency, roundToTwoDecimals } from 'utils/amountUtils';
import { PaymentMethods } from 'constants/common';
import MerchantOrTeamBlockedInfoPanel from 'components/merchant-or-team-blocked/MerchantOrTeamBlockedInfoPanel';
import { useLazyGetCustomerCardsQuery } from 'containers/customers/api';
import routesPath from 'routes/RoutesPath';
import { showNotification } from 'utils/Notifier';

interface OutletContext {
  handleSidePanelClose: () => void;
}

export const CollectPaymentPanel: React.FC = () => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);
  const isOwner = useAppSelector(selectIsOwner);
  const isAdmin = useAppSelector(selectIsAdmin);
  const location = useLocation();
  const trackingResponse = location.state?.trackingResponse;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { allowAction: allowManualKeyIn } = useCommunicationsControl('MANUAL_KEY_IN');
  const { allowAction: allowInvoiceCollectLink } = useCommunicationsControl('INVOICE_COLLECT_LINK');

  const {
    invoiceId,
    rootInvoiceId,
    rootReceiptId,
    rootPage,
    rootCustomerId,
    rootDisputeId,
    rootJobId,
    rootEstimateId
  } = useParams();
  const { handleSidePanelClose } = useOutletContext<OutletContext>();

  const [downloadInvoice] = useLazyDownloadInvoiceAsPDFQuery();
  const {
    invoiceData,
    isSuccess: invoiceDataSuccess,
    isLoading: fetchingInvoiceData
  } = useGetInvoiceDataById(invoiceId || rootInvoiceId);
  const {
    data: merchantCustomisations,
    isSuccess: merchantCustomisationsSuccess,
    isFetching: merchantCustomisationsFetching
  } = useGetMerchantCustomizationsQuery({
    teamId: currentTeam.id.toString()
  });
  const [
    getCustomerCards,
    { data: customerCardsDataResponse, isFetching: isLoadingCustomerCards, isSuccess: isSuccessCustomerCards }
  ] = useLazyGetCustomerCardsQuery();
  const customerCardsData = customerCardsDataResponse || [];

  const { paymentModes } = merchantCustomisations ?? {};
  const [isRootState, setIsRootState] = useState(true);
  const [openManualKeyInModal, setOpenManualKeyInModal] = useState<boolean>(false);
  const [showEditAmountView, setShowEditAmountView] = useState<boolean>(false);
  const [createCustomAmountTracking, { isLoading: isCreateCustomAmountTrackingLoading }] =
    useCreateCustomAmountTrackingMutation();

  // Get the selected amount from navigation state
  const selectedAmount = location.state?.selectedAmount;

  useEffect(() => {
    if (invoiceData && invoiceData.invoiceCustomer?.id && !isSuccessCustomerCards) {
      getCustomerCards({
        teamId: currentTeam.id,
        customerId: invoiceData.invoiceCustomer.id
      });
    }
  }, [invoiceData, currentTeam.id, getCustomerCards]);

  useEffect(() => {
    let routeStateTimeout;
    const routeState: CollectPaymentRouteState = location.state ?? {};
    if (routeState?.fromSidePanel) {
      setIsRootState(false);
    }
    if (routeState?.showEditAmount) {
      setShowEditAmountView(true);
    }
    if (routeState?.shouldAnimate) {
      routeStateTimeout = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: DEFAULT_SIDE_PANEL_ROUTE_STATE });
      }, 800);
    }
    return () => {
      clearTimeout(routeStateTimeout);
    };
  }, [location]);

  const handleSidePanelBack = () => {
    if (isRootState) {
      handleSidePanelClose();
    } else if (location.state?.routedFromSecondaryPanel) {
      if (location.state?.counter !== undefined) {
        navigate(-location.state.counter);
      } else {
        navigate(-2);
      }
    } else {
      // Simple browser back
      navigate(-1);
    }
  };
  const handleCloseSuccessModalBack = () => {
    if (isRootState) {
      handleSidePanelClose();
    } else if (location.state?.routedFromSecondaryPanel) {
      navigate(-((location.state?.counter || 1) + 1));
    } else {
      // Simple browser back - will go to SharePending since EditAmount was replaced in history
      navigate(-1);
    }
  };

  const handleEditAmountClick = () => {
    // Show edit amount view within the same panel
    setShowEditAmountView(true);
  };

  const handleEditAmountBack = () => {
    if (isRootState) {
      handleSidePanelClose();
    } else if (location.state?.showEditAmount) {
      navigate(-1);
    } else {
      setShowEditAmountView(false);
    }
  };

  const handleEditAmountSave = async (amount: number, isCustomAmount: boolean) => {
    // Validate amount
    if (amount <= 0) {
      showNotification(dispatch, NotifierTypes.ERROR, 'Please enter a valid amount greater than 0');
      return;
    }

    const totalAmount = Number(invoiceData?.pendingAmount);
    if (amount > totalAmount) {
      showNotification(
        dispatch,
        NotifierTypes.ERROR,
        `Amount cannot exceed the remaining amount of ${getAmountWithCurrency(roundToTwoDecimals(totalAmount))}`
      );
      return;
    }

    // Handle API call for custom amounts
    let customTrackingResponse = undefined;
    if (isCustomAmount) {
      try {
        customTrackingResponse = await createCustomAmountTracking({
          invoiceId: invoiceData?.invoiceId,
          requestedAmount: amount.toString(),
          teamId: currentTeam?.id?.toString()
        }).unwrap();
      } catch (error) {
        // If tracking API fails, still proceed with original flow
        customTrackingResponse = undefined;
      }
    }

    // Update the selected amount and navigate back to collect payment view with replace: true
    const isNotRemainingAmount = amount !== Number(invoiceData?.pendingAmount);
    const routeState = {
      ...FROM_SIDE_PANEL_ROUTE_STATE,
      routedFromSecondaryPanel: location.state?.routedFromSecondaryPanel || false,
      selectedAmount: isNotRemainingAmount ? amount : undefined,
      trackingResponse: customTrackingResponse,
      counter: location.state?.counter
    };

    // Navigate to same route with updated state (replace: true)
    navigate(location.pathname, { state: routeState, replace: true });
    setShowEditAmountView(false);
  };

  const handlePayWithQRCodeClick = () => {
    if (!allowInvoiceCollectLink()) return;
    const routeState = {
      ...FROM_SIDE_PANEL_ROUTE_STATE,
      selectedAmount: selectedAmount
    };
    if (rootInvoiceId) {
      if (invoiceId) {
        navigate(
          `/invoices/${currentTeam?.id}/${rootInvoiceId}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`,
          {
            state: routeState
          }
        );
      } else {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-pending-invoice/share-qr`, {
          state: routeState
        });
      }
    } else if (rootPage) {
      navigate(
        `/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`,
        {
          state: routeState
        }
      );
    } else if (rootReceiptId) {
      navigate(
        `/payments/receipts/${currentTeam?.id}/${rootReceiptId}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`,
        {
          state: routeState
        }
      );
    } else if (rootCustomerId) {
      navigate(
        `/customers/${currentTeam?.id}/${rootCustomerId}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`,
        {
          state: routeState
        }
      );
    } else if (rootDisputeId) {
      navigate(
        `/payments/disputes/${currentTeam?.id}/${rootDisputeId}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`,
        {
          state: routeState
        }
      );
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`, {
        state: routeState
      });
    } else if (rootEstimateId) {
      navigate(
        `/estimates/${currentTeam?.id}/${rootEstimateId}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`,
        {
          state: routeState
        }
      );
    }
  };

  const handleInvoiceShareClick = () => {
    if (!allowInvoiceCollectLink()) return;
    const routeState = {
      ...FROM_SIDE_PANEL_ROUTE_STATE,
      selectedAmount: selectedAmount,
      trackingResponse: trackingResponse
    };
    if (rootInvoiceId) {
      if (invoiceId) {
        navigate(
          `/invoices/${currentTeam?.id}/${rootInvoiceId}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`,
          {
            state: routeState
          }
        );
      } else {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-pending-invoice/share-url`, {
          state: routeState
        });
      }
    } else if (rootPage) {
      navigate(
        `/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`,
        {
          state: routeState
        }
      );
    } else if (rootReceiptId) {
      navigate(
        `/payments/receipts/${currentTeam?.id}/${rootReceiptId}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`,
        {
          state: routeState
        }
      );
    } else if (rootCustomerId) {
      navigate(
        `/customers/${currentTeam?.id}/${rootCustomerId}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`,
        {
          state: routeState
        }
      );
    } else if (rootDisputeId) {
      navigate(
        `/payments/disputes/${currentTeam?.id}/${rootDisputeId}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`,
        {
          state: routeState
        }
      );
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`, {
        state: routeState
      });
    } else if (rootEstimateId) {
      navigate(
        `/estimates/${currentTeam?.id}/${rootEstimateId}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`,
        {
          state: routeState
        }
      );
    }
  };

  const handleShareInvoiceOnPaymentSuccess = () => {
    if (rootInvoiceId) {
      if (invoiceId) {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-invoice/${invoiceData?.invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE,
          replace: true
        });
      } else {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-invoice`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE,
          replace: true
        });
      }
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    }
  };

  const handleManualKeyIn = () => {
    if (!allowManualKeyIn()) return;
    const manualKeyIn = paymentModes.find(mode => mode.name === PaymentMethods.MANUAL_KEY_IN);
    const featureStatus = checkFeatureStatus(manualKeyIn, isOwner, isAdmin, 'manualKeyIn');

    if (!featureStatus.isEnabled) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: featureStatus.message
          },
          type: NotifierTypes.ERROR,
          showClose: true
        })
      );
      return;
    }
    const amountPayable = selectedAmount || invoiceData?.pendingAmount;

    if (+amountPayable <= +manualKeyIn.maximumAmount) {
      setOpenManualKeyInModal(true);
    } else {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: `Manual card entry can't be used for this payment. 
                            It's available only for amounts up to 
                            $${formatDecimal(formatAmount(+manualKeyIn.maximumAmount))}. 
                            Please use a different method or contact support for help.`
          },
          type: NotifierTypes.ERROR,
          showClose: true
        })
      );
    }
  };

  const handleMarkAsFullyPaid = () => {
    const isFromSecondaryPanel = location.state?.routedFromSecondaryPanel;
    const routeState = {
      ...FROM_SIDE_PANEL_ROUTE_STATE,
      routedFromSecondaryPanel: true,
      selectedAmount: selectedAmount,
      counter: isFromSecondaryPanel ? 3 : undefined
    };
    if (rootInvoiceId) {
      if (invoiceId) {
        navigate(
          routesPath.INVOICE_RECORD_PAYMENT_INVOICE.replace(':teamId', currentTeam?.id.toString() || '')
            .replace(':rootInvoiceId', rootInvoiceId)
            .replace(':invoiceId', invoiceId),
          { state: routeState }
        );
      } else {
        navigate(
          routesPath.INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '').replace(
            ':rootInvoiceId',
            rootInvoiceId
          ),
          { state: routeState }
        );
      }
    } else if (rootJobId) {
      navigate(
        routesPath.JOB_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootJobId', rootJobId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/record-payment/${invoiceId}`, {
        state: routeState
      });
    } else if (rootReceiptId) {
      navigate(
        routesPath.RECEIPT_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootReceiptId', rootReceiptId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    } else if (rootCustomerId) {
      navigate(
        routesPath.CUSTOMER_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootCustomerId', rootCustomerId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    } else if (rootDisputeId) {
      navigate(
        routesPath.DISPUTE_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootDisputeId', rootDisputeId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    } else if (rootEstimateId) {
      navigate(
        routesPath.ESTIMATE_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootEstimateId', rootEstimateId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    }
  };

  const downloadFile = async () => {
    dispatch(
      showNotifier({
        message: {
          primaryMessage: 'Downloading started in the background',
          secondaryMessage: ''
        },
        type: NotifierTypes.INFO,
        showClose: false,
        fontStyle: 'text-primary font-normal'
      })
    );
    await downloadInvoice({ invoiceId: invoiceData.invoiceId });
  };

  const handleManualKeyInClose = () => {
    setOpenManualKeyInModal(false);
  };

  const checkoutUrlWithTracking = trackingResponse?.trackingId
    ? `${invoiceData?.checkoutURL}&trackingId=${trackingResponse.trackingId}`
    : invoiceData?.checkoutURL;

  return (
    <>
      <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={location?.state?.shouldAnimate}>
        {isActionsBlocked ? (
          <MerchantOrTeamBlockedInfoPanel onBack={handleSidePanelBack} />
        ) : (
          <div className="h-full w-96 px-[26px] pt-[30px]">
            {(fetchingInvoiceData || merchantCustomisationsFetching || isLoadingCustomerCards) && (
              <div className="flex h-full w-full items-center justify-center">
                <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
              </div>
            )}
            {!fetchingInvoiceData && invoiceDataSuccess && isSuccessCustomerCards && merchantCustomisationsSuccess && (
              <>
                {showEditAmountView ? (
                  <div className="flex h-full w-full flex-col">
                    <div
                      className="mb-4 flex cursor-pointer items-center gap-1.5 text-17px font-semibold text-primaryText"
                      onClick={handleEditAmountBack}>
                      {isRootState ? (
                        <CrossIconvector className="shrink-0" />
                      ) : (
                        <ArrowRightIcon className="rotate-180" />
                      )}
                      Edit amount
                    </div>
                    <EditAmountMainSection
                      invoiceData={invoiceData}
                      onSave={handleEditAmountSave}
                      initialAmount={selectedAmount}
                      isSaveLoading={isCreateCustomAmountTrackingLoading}
                    />
                  </div>
                ) : (
                  <>
                    <div
                      className="mb-10 flex cursor-pointer items-center gap-1.5 text-17px font-semibold text-primaryText"
                      onClick={handleSidePanelBack}>
                      {isRootState ? (
                        <CrossIconvector className="shrink-0" />
                      ) : (
                        <ArrowRightIcon className="rotate-180" />
                      )}
                      Collect payment
                    </div>
                    <SharePendingInvoiceMainSection
                      invoiceData={invoiceData}
                      handlePayWithQRCodeClick={handlePayWithQRCodeClick}
                      handleRecordPayment={handleMarkAsFullyPaid}
                      handleManualKeyIn={handleManualKeyIn}
                      handleSendInvoice={handleInvoiceShareClick}
                      handleDownloadInvoice={downloadFile}
                      customerCardsData={customerCardsData}
                      isLoadingCards={isLoadingCustomerCards}
                      closeSuccessModalCallback={handleCloseSuccessModalBack}
                      paymentModes={paymentModes}
                      handleSendInvoiceOnPaymentSuccess={handleShareInvoiceOnPaymentSuccess}
                      customAmount={selectedAmount}
                      handleEditAmountClick={handleEditAmountClick}
                      downloadAsLink={true}
                      isForCollectPayment={true}
                    />
                  </>
                )}
              </>
            )}
          </div>
        )}
      </SidePanel>
      {openManualKeyInModal && (
        <ManualKeyInModal
          onClose={handleManualKeyInClose}
          invoiceId={invoiceData.invoiceId}
          checkoutUrl={checkoutUrlWithTracking}
        />
      )}
    </>
  );
};

export default CollectPaymentPanel;
